// @ts-nocheck
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { EntityLink } from "@/components/shared/EntityLink";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Edit,
  Trash2,
  Repeat,
  Clock,
  Calculator,
  RefreshCw,
  Play,
  CheckCircle2,
  Timer,
  AlertCircle,
  Truck,
  ArrowRight,
  ArrowLeft,
  Calendar,
  ChevronDown,
  Package,
  Printer,
  Wrench,
  FileCheck,
  Zap,
  Layers,
  ShieldCheck,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ProcessGroupBadge } from "@/components/shared/ProcessGroupBadge";
import { useNavigate } from "@tanstack/react-router";
import { trpc, trpcClient } from "@/lib/trpc";
import { format } from "date-fns";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { getScheduleConflicts, describeScheduleConflict, type ScheduleConflict } from "@/lib/schedule-conflicts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { NumericInput } from "@/components/ui/numeric-input";
import { ComponentGroupRow } from "../components/ComponentGroupRow";
import { componentRankMap, isComponentTreeMode, orderComponentTree } from "../utils/component-tree";
import { formatNumber, cn } from "@/lib/utils";
import { Lock, AlertTriangle, Sliders, Check } from "lucide-react";
import { evaluateInkCuringDelay } from "@apv-erp/status";

export function formatForDateTimeLocal(isoString?: string | null): string {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function formatDurationMinutes(minutes?: number | null): string {
  if (!minutes || minutes <= 0) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function POProcessTab({
  orderId,
  components = [],
  routingSteps = [],
  qtyOrder = 0,
  qtyPlan = 0,
  qtyAllowance,
  ups = 1,
  runSheetsNeeded,
  productionOrder,
  isPlanLocked = false,
  highlightStepId,
  onNavigateSection,
  onNavigateTab,
}: {
  orderId: string;
  components?: any[];
  routingSteps?: any[];
  qtyOrder?: number;
  qtyPlan?: number;
  qtyAllowance?: number;
  ups?: number;
  runSheetsNeeded?: number;
  productionOrder?: any;
  isPlanLocked?: boolean;
  highlightStepId?: string | null;
  onNavigateSection?: (sectionId: string) => void;
  onNavigateTab?: (tab: string) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internalHighlightStepId, setInternalHighlightStepId] = useState<string | null>(highlightStepId || null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Sửa nhanh gây chồng lịch máy: chờ planner xác nhận (máy chủ trả 409 + danh sách việc bị chồng)
  const [pendingOverlap, setPendingOverlap] = useState<{
    stepId: string;
    patch: Record<string, any>;
    conflicts: ScheduleConflict[];
  } | null>(null);

  // Machine query for inline selection
  const { data: machinesData } = useQuery(
    trpc.machines.list.queryOptions({ limit: 100 })
  );
  const machinesList: any[] =
    (machinesData as any)?.items ||
    (machinesData as any)?.data ||
    (Array.isArray(machinesData) ? machinesData : []) ||
    [];

  // Machine collision detection query
  const { data: collisionsData, refetch: refetchCollisions } = useQuery(
    trpc.productionOrders.detectMachineCollisions.queryOptions(
      { id: orderId },
      { enabled: Boolean(orderId) }
    )
  );
  const collisions: any[] = (collisionsData as any)?.collisions || [];

  // Auto-scroll to highlighted step when selected from ribbon or props
  React.useEffect(() => {
    if (highlightStepId) {
      setInternalHighlightStepId(highlightStepId);
      const el = document.getElementById(`step-row-${highlightStepId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [highlightStepId]);

  const handleStepClick = (stepId: string) => {
    setInternalHighlightStepId(stepId);
    const el = document.getElementById(`step-row-${stepId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // WIP Balance query for this PO
  const { data: wipBalanceData } = useQuery(
    trpc.wipTransfer.getWipBalanceByPO.queryOptions(
      { productionOrderId: orderId },
      { enabled: !!orderId }
    )
  );

  // Quick Handover Dialog state
  const [isHandoverOpen, setIsHandoverOpen] = useState(false);
  const [handoverStep, setHandoverStep] = useState<any>(null);
  const [handoverNextStep, setHandoverNextStep] = useState<any>(null);
  const [handoverForm, setHandoverForm] = useState({
    quantity: 0,
    palletCode: "",
    senderName: "",
    notes: "",
  });

  const handleOpenHandover = (step: any, nextStep: any, holdingQty: number) => {
    setHandoverStep(step);
    setHandoverNextStep(nextStep);
    setHandoverForm({
      quantity: holdingQty,
      palletCode: "",
      senderName: "",
      notes: `Chuyển BTP từ ${step.name} sang ${nextStep.name}`,
    });
    setIsHandoverOpen(true);
  };

  const handleConfirmHandover = async () => {
    if (!handoverStep) return;
    try {
      setIsSubmitting(true);
      await trpcClient.wipTransfer.quickHandover.mutate({
        productionOrderId: orderId,
        fromRoutingStepId: handoverStep.id,
        quantity: Number(handoverForm.quantity) || 0,
        palletCode: handoverForm.palletCode || undefined,
        senderName: handoverForm.senderName || undefined,
        notes: handoverForm.notes || undefined,
      });
      toast.success(
        `Đã bàn giao ${formatNumber(handoverForm.quantity)} BTP sang ${handoverNextStep?.name || "công đoạn sau"}`
      );
      setIsHandoverOpen(false);
      queryClient.invalidateQueries({ queryKey: ["wip-balance-po", orderId] });
      queryClient.invalidateQueries({ queryKey: ["wip-transfers"] });
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err.message || "Không thể bàn giao BTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check-out Dialog state
  const [isCheckOutOpen, setIsCheckOutOpen] = useState(false);
  const [activeCheckOutStep, setActiveCheckOutStep] = useState<any>(null);
  const [checkOutForm, setCheckOutForm] = useState({
    actualQtyOk: 0,
    actualQtyNg: 0,
    runTimeMinutes: 60,
    setupTimeMinutes: 15,
    shift: "CA_1",
    notes: "",
  });

  // Flatten routing steps from components OR fallback to top-level order routingSteps
  const compSteps = (components || []).filter((c: any) => !c.isDeleted).flatMap((c) =>
    (c.routingSteps || []).map((step: any) => ({
      ...step,
      componentId: step.componentId || c.id,
      componentName: c.componentName || "Cấu trúc SP",
    }))
  );

  // Cây Sản phẩm → Hợp phần: nhiều hợp phần thì nhóm công đoạn theo hợp phần (cha trước con)
  const treeMode = isComponentTreeMode(components);
  const componentNodes = orderComponentTree(
    components,
    (productionOrder?.productionOrderSources || []).map((s: any) => s.id)
  );
  const componentRank = componentRankMap(componentNodes);
  const nodeById = new Map(componentNodes.map((n) => [n.id, n]));

  const rawSteps = compSteps.length > 0
    ? compSteps
    : (routingSteps || []).map((step: any) => ({
        ...step,
        componentName: "Thành phần chính",
      }));

  const allRoutingSteps = rawSteps.sort(
    (a: any, b: any) =>
      (treeMode ? componentRank(a.componentId) - componentRank(b.componentId) : 0) ||
      (a.stepOrder || 0) - (b.stepOrder || 0)
  );

  const po = productionOrder;
  const effectiveQtyOrder = Number(po?.qtyOrder ?? qtyOrder) || 0;
  const effectiveQtyAllowance = Number(po?.qtyAllowance ?? qtyAllowance ?? Math.max(0, (qtyPlan || 0) - effectiveQtyOrder)) || 0;
  const effectiveQtyPlan = Number(po?.qtyPlan ?? qtyPlan ?? (effectiveQtyOrder + effectiveQtyAllowance)) || 0;
  const effectiveUps = Number(po?.ups ?? ups ?? 1) || 1;
  const effectiveRunSheets = Number(po?.runSheetsNeeded ?? runSheetsNeeded ?? po?.totalPlanSheets ?? Math.ceil((effectiveQtyPlan || effectiveQtyOrder || 1) / effectiveUps)) || 0;
  const allowancePct = effectiveQtyOrder > 0 ? ((effectiveQtyAllowance / effectiveQtyOrder) * 100).toFixed(1) : '0';

  // ── 4 TRỤ CỘT SẴN SÀNG LÊN MÁY (PRE-FLIGHT READINESS) ──
  const toolings = po?.toolings || [];
  const plateToolings = toolings.filter(
    (t: any) => t.toolingType === 'PLATE' || t.toolingType === 'ZINC'
  );
  const moldToolings = toolings.filter(
    (t: any) => t.toolingType !== 'PLATE' && t.toolingType !== 'ZINC'
  );

  // 1. Vật tư
  const isMaterialReady = po?.materialStatus === 'READY';

  // 2. Kẽm CTP
  const allPlatesReady =
    plateToolings.length > 0
      ? plateToolings.every(
          (p: any) =>
            p.status === 'CTP_READY' ||
            p.readinessStatus === 'READY' ||
            (p.notes && p.notes.includes('CTP_READY'))
        )
      : po?.plateStatus === 'READY' || po?.isPlateExported;
  const isPlateBurning =
    po?.plateStatus === 'BURNING' ||
    plateToolings.some((p: any) => p.notes && p.notes.includes('BURNING'));
  const isPlateReady = allPlatesReady || po?.plateStatus === 'READY';

  // 3. Khuôn gá
  const allMoldsReady =
    moldToolings.length > 0
      ? moldToolings.every((m: any) => m.readinessStatus === 'READY')
      : po?.toolingStatus === 'READY' || po?.moldStatus === 'READY';
  const isToolingReady = allMoldsReady || po?.toolingStatus === 'READY';

  // 4. File in & TCKT
  const isFileReady =
    po?.isFileApproved || Boolean(po?.techSpecs || po?.technicalSpec || po?.techSpecId);

  // Đủ điều kiện toàn bộ
  const isAllReady = isMaterialReady && isPlateReady && isToolingReady && isFileReady;

  const [isQuickClearing, setIsQuickClearing] = useState(false);
  const handleQuickClear = async () => {
    if (!po?.id) return;
    try {
      setIsQuickClearing(true);
      const cleared: any = await trpcClient.productionOrders.quickClearReadiness.mutate({
        id: po.id,
        notes: 'Quản đốc duyệt khẩn cấp điều kiện lên máy (Pre-Flight Quick Clear)',
      });
      toast.success(cleared?.message || "Đã duyệt vật tư, khuôn, file.");
      queryClient.invalidateQueries({ queryKey: ['production-order', po.id] });
      queryClient.invalidateQueries({ queryKey: ['production-order', orderId] });
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi duyệt sẵn sàng');
    } finally {
      setIsQuickClearing(false);
    }
  };

  // Suy diễn đơn vị tính nếu chưa có: In ấn/cán màng/phủ/lụa là SHEET; bế/dán/đóng gói là PCS
  const inferUnit = (s: any): 'SHEET' | 'PCS' => {
    if (s.productionUnit === 'SHEET' || s.productionUnit === 'PCS') {
      return s.productionUnit;
    }
    const name = (s.name || s.process?.name || '').toLowerCase();
    if (name.includes('in') || name.includes('cán') || name.includes('can') || 
        name.includes('màng') || name.includes('mang') || name.includes('phủ') || 
        name.includes('lụa') || name.includes('nhũ') || name.includes('cắt giấy') ||
        name.includes('xả') || name.includes('tráng')) {
      return 'SHEET';
    }
    if (name.includes('dán') || name.includes('dan') || name.includes('gói') || 
        name.includes('goi') || name.includes('qc') || name.includes('kcs') || 
        name.includes('đóng') || name.includes('thành phẩm')) {
      return 'PCS';
    }
    return 'SHEET';
  };

  // Thuật toán gối đầu sản lượng liên công đoạn (Cascading Yield Engine):
  // "Số lượng thành phẩm của công đoạn trước là số lượng đầu vào công đoạn sau nếu cùng đơn vị sheets, nếu pcs thì nhân với số ups để quy ra."
  const computedSteps = React.useMemo(() => {
    let prevStepOutput = effectiveRunSheets;
    let prevStepUnit: 'SHEET' | 'PCS' = 'SHEET';
    let prevStepName = '';

    return allRoutingSteps.map((step: any, index: number) => {
      const unit = inferUnit(step);
      const setupWaste = Number(step.plannedSetupWaste) || 0;
      const runWastePct = Number(step.plannedRunWastePct) || 0;

      let inputQty = 0;
      let originNote = '';
      // Mỗi hợp phần là một dòng chảy riêng: công đoạn đầu của hợp phần nhận phôi cấp, không nối từ hợp phần trước
      const startsFlow =
        index === 0 || (treeMode && step.componentId !== allRoutingSteps[index - 1]?.componentId);

      if (startsFlow) {
        if (unit === 'SHEET') {
          inputQty = step.plannedInputQty || effectiveRunSheets;
          originNote = 'Phôi in cấp đầu vào';
        } else {
          inputQty = step.plannedInputQty || effectiveQtyPlan || effectiveQtyOrder;
          originNote = 'Kế hoạch phôi cấp';
        }
      } else {
        if (prevStepUnit === 'SHEET' && unit === 'SHEET') {
          inputQty = prevStepOutput;
          originNote = `← ${formatNumber(prevStepOutput)} tờ từ [${prevStepName}]`;
        } else if (prevStepUnit === 'SHEET' && unit === 'PCS') {
          inputQty = prevStepOutput * effectiveUps;
          originNote = `← ${formatNumber(prevStepOutput)} tờ × ${effectiveUps} UPS từ [${prevStepName}]`;
        } else if (prevStepUnit === 'PCS' && unit === 'SHEET') {
          inputQty = Math.ceil(prevStepOutput / effectiveUps);
          originNote = `← ${formatNumber(prevStepOutput)} pcs / ${effectiveUps} UPS từ [${prevStepName}]`;
        } else {
          inputQty = prevStepOutput;
          originNote = `← ${formatNumber(prevStepOutput)} pcs từ [${prevStepName}]`;
        }
      }

      const runWasteQty = Math.ceil(inputQty * (runWastePct / 100));
      const totalWasteQty = setupWaste + runWasteQty;
      const isCompleted = step.status === 'COMPLETED';
      const actualOk = Number(step.actualQtyOk) || Number(step.actualQuantity) || 0;
      const outputQty = (isCompleted && actualOk > 0)
        ? actualOk
        : Math.max(0, inputQty - totalWasteQty);

      prevStepOutput = outputQty;
      prevStepUnit = unit;
      prevStepName = step.name || step.process?.name || `Bước ${index + 1}`;

      return {
        ...step,
        unit,
        cascadingInputQty: inputQty,
        cascadingOriginNote: originNote,
        cascadingTotalWasteQty: totalWasteQty,
        cascadingRunWasteQty: runWasteQty,
        cascadingOutputQty: outputQty,
        targetQty: inputQty,
      };
    });
  }, [allRoutingSteps, treeMode, effectiveRunSheets, effectiveQtyPlan, effectiveQtyOrder, effectiveUps]);

  const handleEdit = (step: any) => {
    setFormData({ ...step });
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setFormData({
      name: "",
      stepOrder: allRoutingSteps.length + 1,
      componentId: components[0]?.id || "",
    });
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await trpcClient.productionOrders.removeRoutingStep.mutate({
        id: orderId,
        stepId: selectedId,
      });

      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      queryClient.invalidateQueries({
        queryKey: ["production-orders"],
      });
      toast.success("Đã xóa công đoạn");
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi xóa công đoạn");
    } finally {
      setIsDeleteOpen(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.componentId && components.length > 0) {
      formData.componentId = components[0].id;
    }
    try {
      setIsSubmitting(true);
      if (formData.id) {
        await trpcClient.productionOrders.updateRoutingStep.mutate({
          id: orderId,
          stepId: formData.id,
          data: formData,
        });
      } else {
        await trpcClient.productionOrders.addRoutingStep.mutate({
          id: orderId,
          data: formData,
        });
      }
      setIsFormOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      queryClient.invalidateQueries({
        queryKey: ["production-orders"],
      });
      toast.success("Lưu công đoạn thành công");
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi lưu");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Row-level Check-in (Start)
  const handleCheckIn = async (step: any) => {
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.checkInStep.mutate({
        productionOrderId: orderId,
        stepId: step.id,
        machineId: step.machineId || undefined,
        operatorId: step.operatorId || undefined,
      });
      toast.success(`Đã Check-in bắt đầu công đoạn: ${step.name}`);
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      queryClient.invalidateQueries({
        queryKey: ["production-orders"],
      });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi check-in công đoạn");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Check-out Dialog
  const handleOpenCheckOut = (step: any) => {
    setActiveCheckOutStep(step);
    const target = step.standardQuantity || qtyPlan || qtyOrder || 1000;
    setCheckOutForm({
      actualQtyOk: target,
      actualQtyNg: 0,
      runTimeMinutes: Math.round((step.estDurationHours || 1) * 60),
      setupTimeMinutes: step.plannedSetupTime || 15,
      shift: "CA_1",
      notes: `Hoàn thành công đoạn ${step.name}`,
    });
    setIsCheckOutOpen(true);
  };

  // Submit Check-out
  const handleConfirmCheckOut = async () => {
    if (!activeCheckOutStep) return;
    try {
      setIsSubmitting(true);
      await trpcClient.productionOrders.checkOutStep.mutate({
        productionOrderId: orderId,
        stepId: activeCheckOutStep.id,
        actualQtyOk: Number(checkOutForm.actualQtyOk) || 0,
        actualQtyNg: Number(checkOutForm.actualQtyNg) || 0,
        runTimeMinutes: Number(checkOutForm.runTimeMinutes) || 0,
        setupTimeMinutes: Number(checkOutForm.setupTimeMinutes) || 0,
        shift: checkOutForm.shift,
        notes: checkOutForm.notes,
      });
      toast.success(`Check-out thành công công đoạn ${activeCheckOutStep.name}! Đã tự động cập nhật sản lượng và nhật ký.`);
      setIsCheckOutOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      queryClient.invalidateQueries({
        queryKey: ["production-orders"],
      });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi check-out công đoạn");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInstantPatchRoutingStep = async (stepId: string, patchData: Record<string, any>) => {
    if (!stepId) return;
    try {
      await trpcClient.productionOrders.updateRoutingStepInline.mutate({
        stepId,
        data: patchData,
      });
      setPendingOverlap(null);
      await queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["production-orders"],
      });
      refetchCollisions();
      toast.success(patchData.confirmOverlap ? "Đã lưu và khóa lịch công đoạn (chấp nhận chồng lịch)" : "Đã lưu cập nhật KHSX");
    } catch (error: any) {
      const conflicts = getScheduleConflicts(error);
      if (conflicts) {
        setPendingOverlap({ stepId, patch: patchData, conflicts });
        return;
      }
      toast.error(error.message || "Lỗi khi cập nhật công đoạn");
    }
  };

  const handleCalculateAPS = async (direction: 'FORWARD' | 'BACKWARD' = 'FORWARD') => {
    try {
      setIsSubmitting(true);
      const res = await trpcClient.productionOrders.calculateAPSSchedule.mutate({
        id: orderId,
        direction,
      });
      if (res.success) {
        const msg = res.message || (direction === 'BACKWARD' ? "Đã lập lịch gối đầu APS đi lùi thành công" : "Đã lập lịch gối đầu APS đi tới thành công");
        if ((res as any).lateDays > 0) toast.warning(msg);
        else toast.success(msg);
        await queryClient.invalidateQueries({
          queryKey: ["production-order"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["production-orders"],
        });
        refetchCollisions();
      }
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi lập lịch gối đầu APS");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerateFromMaster = async () => {
    try {
      setIsSubmitting(true);
      const res = await trpcClient.productionOrders.regenerateRoutingFromMaster.mutate({
        id: orderId,
      });
      toast.success(`Đã nạp lại ${res.count} công đoạn từ SP Master`);
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      queryClient.invalidateQueries({
        queryKey: ["production-orders"],
      });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi nạp lại quy trình từ Master Data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="section-operations" className="space-y-3">
      {/* ── CARD HEADER: TÊN BẢNG, CHỈ BÁO XUNG ĐỘT, NÚT SỬA NHANH & LẬP LỊCH APS ── */}
      <div className="flex justify-between items-center flex-wrap gap-2 pt-1">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Repeat className="h-5 w-5 text-indigo-600" />
            Kế hoạch sản xuất
          </h3>

          {/* Badge cảnh báo trùng lịch máy nếu có */}
          {collisions.length > 0 && (
            <Badge
              variant="destructive"
              className="text-[10px] font-bold py-0.5 px-2 font-mono gap-1 animate-pulse"
              title="Phát hiện máy sản xuất bị xếp trùng khung giờ với LSX khác"
            >
              <AlertTriangle className="h-3 w-3" />
              <span>{collisions.length} xung đột trùng máy</span>
            </Badge>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap items-center">
          {/* Nút Toggle Chế Độ Chỉnh Sửa Nhanh Inline (Rule Phase 227) */}
          <Button
            size="sm"
            variant={isEditMode ? "default" : "outline"}
            className={cn(
              "text-xs h-7.5 gap-1.5 font-semibold transition-all",
              isEditMode
                ? "bg-amber-600 hover:bg-amber-700 text-white shadow-xs"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            )}
            onClick={() => setIsEditMode(!isEditMode)}
            disabled={isPlanLocked}
            data-testid="btn-toggle-inline-edit-mode"
          >
            {isEditMode ? (
              <>
                <Check className="h-3.5 w-3.5 text-white" />
                <span>Đang mở chế độ sửa</span>
              </>
            ) : (
              <>
                <Edit className="h-3.5 w-3.5 text-slate-600" />
                <span>Sửa nhanh KHSX</span>
              </>
            )}
          </Button>

          {/* Dropdown Lập Lịch Gối Đầu Thông Minh APS 2 Chiều */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100 text-xs h-7.5 gap-1 font-semibold"
                disabled={isPlanLocked || isSubmitting}
                data-testid="btn-calculate-aps"
              >
                <Clock className="h-3.5 w-3.5 text-purple-700" />
                Lập lịch gối đầu
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuItem
                onClick={() => handleCalculateAPS("BACKWARD")}
                className="text-xs cursor-pointer gap-2 py-2"
                data-testid="btn-aps-backward"
              >
                <ArrowLeft className="h-4 w-4 text-purple-600 shrink-0" />
                <div className="flex flex-col">
                  <span className="font-bold text-slate-800">Từ Deadline giao hàng (Backward)</span>
                  <span className="text-[10px] text-slate-500">Tính ngày bắt đầu muộn nhất để kịp hạn giao; các công đoạn vẫn xếp vào khe trống của máy</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleCalculateAPS("FORWARD")}
                className="text-xs cursor-pointer gap-2 py-2"
                data-testid="btn-aps-forward"
              >
                <ArrowRight className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="flex flex-col">
                  <span className="font-bold text-slate-800">Từ Ngày triển khai (Forward)</span>
                  <span className="text-[10px] text-slate-500">Xếp từng công đoạn vào khe trống sớm nhất của máy theo ca, gối đầu với công đoạn trước</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="sm"
            variant="outline"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs h-7.5"
            onClick={() =>
              navigate({
                to: "/production-scheduling",
                search: { view: "GANTT", highlightOrder: orderId, poStatusFilter: "ALL" } as any,
              })
            }
          >
            <Calendar className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Lịch Máy
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-xs h-7.5"
            onClick={handleRegenerateFromMaster}
            disabled={isPlanLocked || isSubmitting}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Nạp Master
          </Button>

          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-xs h-7.5"
            onClick={handleCreate}
            disabled={isPlanLocked}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Thêm công đoạn
          </Button>
        </div>
      </div>

      {/* ── BANNER CHỈ BÁO KHI ĐANG Ở CHẾ ĐỘ CHỈNH SỬA NHANH INLINE ── */}
      {isEditMode && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1.5 rounded-md text-xs flex items-center justify-between shadow-2xs">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-500 animate-ping shrink-0" />
            <span className="font-semibold">Chế độ sửa nhanh KHSX:</span>
            <span>Các thay đổi về Máy, Setup, Tốc độ, % Gối đầu và Lịch bắt đầu/Hạn chót sẽ tự động lưu ngay khi rời ô (blur).</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsEditMode(false)}
            className="h-6 text-[11px] text-amber-900 hover:bg-amber-100 font-bold shrink-0 ml-2"
          >
            Xong / Đóng
          </Button>
        </div>
      )}

      {/* ── BẢNG KẾ HOẠCH CÔNG ĐOẠN SẢN XUẤT ── */}
      <Card className="shadow-sm border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50 border-b">
              <TableRow>
                <TableHead className="w-10 text-center">TT</TableHead>
                <TableHead className="min-w-[140px]">Công đoạn</TableHead>
                <TableHead className="w-24">Thành phần</TableHead>
                <TableHead className="text-right w-36" title="Số lượng yêu cầu đầu vào kế hoạch (gối đầu theo sheets/pcs)">SL đầu vào</TableHead>
                <TableHead className="text-right w-28 text-slate-600" title="Bù hao Setup &amp; bù hao chạy theo định mức">SL bù hao</TableHead>
                <TableHead className="text-center w-24 text-indigo-700 font-semibold" title="Thời gian chuẩn bị + thời gian chạy định mức">TG định mức</TableHead>
                <TableHead className="text-center w-36 text-slate-700 font-semibold" title="Lịch bắt đầu và deadline hoàn thành công đoạn theo KHSX">Lịch kế hoạch</TableHead>
                <TableHead className="text-right w-28 text-emerald-700 font-semibold">SL đạt</TableHead>
                <TableHead className="text-right w-20 text-rose-600">Phế phẩm</TableHead>
                <TableHead className="text-right w-20 text-amber-700">Tồn BTP</TableHead>
                <TableHead className="w-32 text-center">Tiến độ</TableHead>
                <TableHead className="w-32">Máy / Thợ</TableHead>
                <TableHead className="w-28 text-center">Trạng thái</TableHead>
                <TableHead className="text-right w-48">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {computedSteps.length > 0 ? (
                computedSteps.map((step: any, index: number) => {
                  const reqQty = step.cascadingInputQty || step.plannedInputQty || step.standardQuantity || effectiveQtyPlan || effectiveQtyOrder || 1000;
                  const targetQty = reqQty;
                  const okQty = step.actualQtyOk || step.actualQuantity || (step.status === "COMPLETED" ? targetQty : 0);
                  const ngQty = step.actualQtyNg || 0;
                  const stepPercent = targetQty > 0 ? Math.min(100, Math.round((okQty / targetQty) * 100)) : 0;
                  const isCompleted = step.status === "COMPLETED";
                  const isRunning = step.status === "RUNNING";

                  const setupWaste = step.plannedSetupWaste ?? 0;
                  const runWastePct = step.plannedRunWastePct ?? 0;
                  const totalWaste = step.cascadingTotalWasteQty ?? (setupWaste + Math.ceil(reqQty * (runWastePct / 100)));
                  const unit = step.unit || 'SHEET';
                  const unitLabel = unit === 'SHEET' ? 'tờ' : 'pcs';

                  // Định mức thời gian
                  const normMinutes = step.plannedTotalTime ?? (
                    (step.plannedSetupTime || 0) + (step.plannedSpeed > 0 ? (reqQty / step.plannedSpeed) * 60 : (step.runTimeMinutes || 0))
                  );
                  const durationStr = formatDurationMinutes(normMinutes);

                  // WIP holding balance
                  const balanceSteps = (wipBalanceData as any)?.steps || (Array.isArray(wipBalanceData) ? wipBalanceData : []);
                  const stepBalance = balanceSteps.find(
                    (b: any) => b.stepId === step.id || b.stepOrder === step.stepOrder
                  );
                  const holdingWip = stepBalance ? stepBalance.holdingQty : Math.max(0, okQty);
                  const nextStep = computedSteps[index + 1];
                  const isLastStep = index === computedSteps.length - 1;

                  const isHighlighted = Boolean(
                    internalHighlightStepId && (step.id === internalHighlightStepId || step.stepId === internalHighlightStepId)
                  );

                  // Collision detection for this step
                  const stepCollision = collisions.find(
                    (c: any) => c.stepId === step.id || (step.machineId && c.machineId === step.machineId)
                  );
                  const prevStep = index > 0 ? computedSteps[index - 1] : null;
                  const prevName = String(prevStep?.name || prevStep?.process?.name || "");
                  const currName = String(step.name || step.process?.name || "");
                  const curingEval = evaluateInkCuringDelay(
                    prevName,
                    currName,
                    Boolean(prevName.toUpperCase().includes("UV") || currName.toUpperCase().includes("UV"))
                  );

                  const groupStart =
                    treeMode && (index === 0 || step.componentId !== computedSteps[index - 1]?.componentId);
                  const groupNode = nodeById.get(step.componentId) || null;

                  return (
                    <React.Fragment key={step.id || index}>
                    {groupStart && (
                      <ComponentGroupRow
                        node={groupNode}
                        colSpan={14}
                        count={computedSteps.filter((s: any) => s.componentId === step.componentId).length}
                        unitLabel="công đoạn"
                      />
                    )}
                    <TableRow
                      id={`step-row-${step.id || step.stepId || index}`}
                      className={cn(
                        "hover:bg-slate-50/80 transition-all",
                        isHighlighted && "bg-indigo-50/90 ring-2 ring-indigo-500 ring-inset shadow-xs",
                        stepCollision && "bg-rose-50/40"
                      )}
                    >
                      <TableCell className="text-slate-500 text-center font-mono font-bold text-xs">
                        {step.stepOrder || index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-slate-800 text-xs">
                              {step.name || step.process?.name}
                            </span>
                            {unit === "SHEET" ? (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1.5 py-0 font-medium bg-emerald-50 text-emerald-700 border-emerald-200"
                              >
                                Tờ in lớn
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1.5 py-0 font-medium bg-purple-50 text-purple-700 border-purple-200"
                              >
                                Từng con
                              </Badge>
                            )}
                            {curingEval.curingDelayHours > 0 && (
                              <Badge
                                variant="outline"
                                data-testid="po-process-curing-delay-badge"
                                className="text-[9px] px-1.5 py-0 font-bold bg-amber-50 text-amber-800 border-amber-300"
                                title={curingEval.reason}
                              >
                                ⏳ Chờ khô mực {curingEval.curingDelayHours}h
                              </Badge>
                            )}
                          </div>
                          <ProcessGroupBadge
                            process={step.process}
                            group={step.process?.group}
                            name={step.name || step.process?.name}
                            showDetail={false}
                            size="xs"
                          />
                        </div>
                        {step.technicalRequirements && (
                          <div className="text-[11px] text-slate-500 truncate max-w-[200px] mt-0.5" title={step.technicalRequirements}>
                            {step.technicalRequirements}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {step.componentName}
                      </TableCell>
                      {/* Cột SL Đầu Vào (Yêu Cầu) - Kèm đơn vị và chú giải gối đầu */}
                      <TableCell className="text-right font-mono font-bold text-xs text-slate-800 align-top">
                        <div className="flex items-center justify-end gap-1">
                          <span>{formatNumber(reqQty)}</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[9px] px-1 py-0 font-sans font-bold",
                              unit === "SHEET"
                                ? "bg-sky-50 text-sky-700 border-sky-200"
                                : "bg-emerald-50 text-emerald-700 border-emerald-200"
                            )}
                          >
                            {unitLabel}
                          </Badge>
                        </div>
                        {step.cascadingOriginNote && (
                          <div
                            className="text-[10px] text-slate-400 font-sans font-normal break-words whitespace-normal max-w-[180px] mt-0.5 ml-auto text-right leading-snug"
                            title={step.cascadingOriginNote}
                          >
                            {step.cascadingOriginNote}
                          </div>
                        )}
                      </TableCell>
                      {/* Cột SL Bù Hao */}
                      <TableCell
                        className="text-right font-mono text-[11px] text-slate-600 align-top"
                        title={`Bù hao Setup: ${setupWaste} ${unitLabel} | Bù hao chạy: ${runWastePct}% (= ${formatNumber(step.cascadingRunWasteQty || Math.ceil(reqQty * (runWastePct / 100)))} ${unitLabel})`}
                      >
                        <div className="font-semibold text-rose-700">
                          +{formatNumber(totalWaste)} <span className="text-[10px] font-normal">{unitLabel}</span>
                        </div>
                        {(setupWaste > 0 || runWastePct > 0) && (
                          <div className="text-[10px] text-slate-400">
                            {setupWaste > 0 ? `+${setupWaste}` : ""} {runWastePct > 0 ? `(${runWastePct}%)` : ""}
                          </div>
                        )}
                      </TableCell>

                      {/* Cột TG Định Mức: Đọc hoặc Sửa Nhanh */}
                      <TableCell className={cn(
                        "text-center font-mono text-[11px] align-top",
                        isEditMode ? "bg-amber-50/40 p-1.5" : "text-indigo-700 font-bold bg-indigo-50/30"
                      )}>
                        {isEditMode ? (
                          <div className="flex flex-col gap-1 items-center w-28 mx-auto">
                            <div className="flex items-center gap-1 w-full justify-between">
                              <span className="text-[10px] text-slate-500 font-sans">Setup:</span>
                              <div className="flex items-center gap-0.5">
                                <input
                                  type="number"
                                  min={0}
                                  defaultValue={step.plannedSetupTime ?? 15}
                                  onBlur={(e) => {
                                    const val = Number(e.target.value);
                                    if (val !== step.plannedSetupTime) {
                                      handleInstantPatchRoutingStep(step.id, { plannedSetupTime: val });
                                    }
                                  }}
                                  className="w-14 px-1 py-0.5 text-[11px] text-right font-mono border rounded bg-white text-slate-800 focus:ring-1 focus:ring-amber-500"
                                  title="Thời gian chuẩn bị (phút)"
                                />
                                <span className="text-[9px] text-slate-400">p</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 w-full justify-between">
                              <span className="text-[10px] text-slate-500 font-sans">Tốc độ:</span>
                              <div className="flex items-center gap-0.5">
                                <input
                                  type="number"
                                  min={0}
                                  defaultValue={step.plannedSpeed ?? 0}
                                  onBlur={(e) => {
                                    const val = Number(e.target.value);
                                    if (val !== step.plannedSpeed) {
                                      handleInstantPatchRoutingStep(step.id, { plannedSpeed: val });
                                    }
                                  }}
                                  className="w-14 px-1 py-0.5 text-[11px] text-right font-mono border rounded bg-white text-indigo-700 font-bold focus:ring-1 focus:ring-amber-500"
                                  title="Tốc độ định mức (sp/giờ hoặc tờ/giờ)"
                                />
                                <span className="text-[9px] text-slate-400">/h</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="font-bold text-xs">{durationStr}</div>
                            {(step.plannedSetupTime > 0 || step.plannedSpeed > 0) && (
                              <div className="text-[9px] text-slate-500 font-normal font-sans mt-0.5">
                                {step.plannedSetupTime || 0}p • {formatNumber(step.plannedSpeed || 0)}/h
                              </div>
                            )}
                          </>
                        )}
                      </TableCell>

                      {/* Cột Lịch Kế Hoạch (Bắt đầu - Deadline - Gối đầu): Đọc hoặc Sửa Nhanh */}
                      <TableCell className={cn(
                        "text-center font-mono text-[11px] align-top",
                        isEditMode ? "bg-amber-50/40 p-1.5" : "bg-slate-50/40"
                      )}>
                        {isEditMode ? (
                          <div className="flex flex-col gap-1 w-44 mx-auto text-left">
                            <div className="flex items-center gap-1">
                              <span className="text-[9px] text-slate-500 w-6 shrink-0 font-sans">Từ:</span>
                              <input
                                type="datetime-local"
                                defaultValue={formatForDateTimeLocal(step.plannedStartDate || step.scheduledStartTime)}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  const iso = val ? new Date(val).toISOString() : null;
                                  handleInstantPatchRoutingStep(step.id, { plannedStartDate: iso });
                                }}
                                className="text-[10px] font-mono px-1 py-0.5 border rounded bg-white w-full focus:ring-1 focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[9px] text-slate-500 w-6 shrink-0 font-bold font-sans">Hạn:</span>
                              <input
                                type="datetime-local"
                                defaultValue={formatForDateTimeLocal(step.plannedEndDate || step.scheduledEndTime)}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  const iso = val ? new Date(val).toISOString() : null;
                                  handleInstantPatchRoutingStep(step.id, { plannedEndDate: iso });
                                }}
                                className="text-[10px] font-mono px-1 py-0.5 border rounded bg-white w-full font-bold text-indigo-900 focus:ring-1 focus:ring-amber-500"
                              />
                            </div>
                            <div className="flex items-center justify-end gap-1 mt-0.5">
                              <span className="text-[9px] text-purple-700 font-semibold font-sans">Gối đầu:</span>
                              <input
                                type="number"
                                min={0}
                                max={80}
                                defaultValue={step.overlapPercent ?? 0}
                                onBlur={(e) => {
                                  const val = Math.min(80, Math.max(0, Number(e.target.value) || 0));
                                  if (val !== step.overlapPercent) {
                                    handleInstantPatchRoutingStep(step.id, { overlapPercent: val });
                                  }
                                }}
                                className="w-11 px-1 py-0 text-[10px] text-right font-mono font-bold text-purple-700 border border-purple-300 rounded bg-purple-50 focus:ring-1 focus:ring-purple-500"
                                title="% gối đầu với công đoạn kế tiếp (0 - 80%)"
                              />
                              <span className="text-[9px] text-purple-700 font-medium">%</span>
                            </div>
                          </div>
                        ) : step.plannedEndDate || step.scheduledEndTime ? (
                          <div className="flex flex-col items-center gap-0.5">
                            {(step.plannedStartDate || step.scheduledStartTime) && (
                              <span className="text-slate-500 text-[10px]">
                                Từ: {format(new Date(step.plannedStartDate || step.scheduledStartTime), "dd/MM HH:mm")}
                              </span>
                            )}
                            <span className={cn(
                              "font-bold text-xs",
                              step.isLate || (new Date(step.plannedEndDate || step.scheduledEndTime) < new Date() && !isCompleted)
                                ? "text-rose-600"
                                : "text-indigo-900"
                            )}>
                              Hạn: {format(new Date(step.plannedEndDate || step.scheduledEndTime), "dd/MM HH:mm")}
                            </span>
                            {step.overlapPercent > 0 && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-purple-700 border-purple-300 bg-purple-50 font-sans font-semibold">
                                Gối {step.overlapPercent}%
                              </Badge>
                            )}
                            {(step.isLate || (new Date(step.plannedEndDate || step.scheduledEndTime) < new Date() && !isCompleted)) && (
                              <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[9px] py-0 h-4">
                                Quá hạn
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">Chưa xếp lịch</span>
                        )}
                      </TableCell>


                      {/* Cột SL Đạt (OK) / Thành Phẩm */}
                      <TableCell className="text-right font-mono font-bold text-xs text-emerald-700 bg-emerald-50/40 align-top">
                        <div>
                          {formatNumber(okQty)} <span className="text-[10px] font-normal text-emerald-600">{unitLabel}</span>
                        </div>
                        {okQty > 0 ? (
                          <div className="text-[10px] text-emerald-700 font-medium flex items-center justify-end gap-0.5 mt-0.5" title="Đã ghi nhận sản lượng thực tế từ nhật ký sàn máy">
                            <span>✓ Ghi nhận nhật ký</span>
                          </div>
                        ) : !isCompleted ? (
                          <div className="text-[10px] text-slate-400 font-normal">
                            Dự kiến: {formatNumber(step.cascadingOutputQty || Math.max(0, reqQty - totalWaste))}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-rose-600">
                        {formatNumber(ngQty)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-xs text-amber-700 bg-amber-50/40">
                        {formatNumber(holdingWip)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 w-full px-1">
                          <div className="flex justify-between text-[11px] font-semibold">
                            <span className={cn(isCompleted ? "text-emerald-700" : isRunning ? "text-blue-700" : "text-slate-500")}>
                              {stepPercent}%
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">
                              {okQty}/{targetQty}
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full transition-all duration-300 rounded-full",
                                isCompleted ? "bg-emerald-600" : isRunning ? "bg-blue-600" : "bg-slate-300"
                              )}
                              style={{ width: `${stepPercent}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={cn(isEditMode && "bg-amber-50/40 p-1.5")}>
                        {isEditMode ? (
                          <div className="space-y-1 w-32">
                            <select
                              value={step.machineId || ""}
                              onChange={(e) =>
                                handleInstantPatchRoutingStep(step.id, {
                                  machineId: e.target.value || null,
                                })
                              }
                              disabled={isPlanLocked}
                              className="text-xs font-semibold rounded px-1.5 py-1 border border-slate-300 bg-white text-slate-800 focus:border-amber-500 w-full"
                            >
                              <option value="">-- Chưa gán máy --</option>
                              {machinesList.map((m: any) => (
                                <option key={m.id} value={m.id}>
                                  {m.name || m.code}
                                </option>
                              ))}
                            </select>

                            {stepCollision && (
                              <div
                                className="text-[9px] font-bold text-rose-700 bg-rose-100 border border-rose-300 rounded px-1 py-0.5 flex items-center gap-0.5 truncate"
                                title={`Trùng với LSX ${stepCollision.collidingPoCode} (${stepCollision.collidingStepName})`}
                              >
                                <AlertTriangle className="h-3 w-3 text-rose-600 shrink-0" />
                                <span className="truncate">Trùng: {stepCollision.collidingPoCode}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs space-y-0.5">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-400 font-medium">M:</span>
                              {step.machineId ? (
                                <EntityLink
                                  entityType="machine"
                                  id={step.machineId}
                                  label={step.machine?.name || step.machineId}
                                  search={{ returnUrl: `/production-orders/${orderId}?tab=process` }}
                                  className="font-medium text-slate-800 text-[11px] truncate max-w-[100px]"
                                />
                              ) : (
                                <span className="font-medium text-slate-800 text-[11px] truncate max-w-[100px]">
                                  {step.machine?.name || "-"}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-400 font-medium">O:</span>
                              <span className="text-slate-700 text-[11px] truncate max-w-[100px]">
                                {step.operator?.fullName || step.operatorId || "-"}
                              </span>
                            </div>

                            {stepCollision && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 mt-1 rounded bg-rose-100 text-rose-800 border border-rose-300 text-[10px] font-bold hover:bg-rose-200 cursor-pointer animate-pulse"
                                  >
                                    <AlertTriangle className="h-3 w-3 text-rose-600 shrink-0" />
                                    <span>Trùng máy</span>
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-2.5 text-xs shadow-lg border-rose-200" align="start">
                                  <div className="font-bold text-rose-800 flex items-center gap-1.5 pb-1 border-b border-rose-100">
                                    <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
                                    Trùng lịch máy {stepCollision.machineName}
                                  </div>
                                  <div className="mt-2 space-y-1 text-slate-600 text-[11px]">
                                    <p>
                                      Trùng với: <strong className="text-slate-900">{stepCollision.collidingPoCode}</strong> ({stepCollision.collidingStepName})
                                    </p>
                                    <p className="text-[10px] text-slate-500 font-mono">
                                      Từ {format(new Date(stepCollision.conflictStart), "dd/MM HH:mm")} đến {format(new Date(stepCollision.conflictEnd), "dd/MM HH:mm")}
                                    </p>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <select
                          disabled={isPlanLocked}
                          value={step.status || "PLANNED"}
                          onChange={(e) => handleInstantPatchRoutingStep(step.id, { status: e.target.value })}
                          className={cn(
                            "text-[11px] font-bold rounded px-2 py-1 border cursor-pointer focus:outline-none transition-colors",
                            step.status === "COMPLETED" && "bg-emerald-50 text-emerald-700 border-emerald-300",
                            step.status === "RUNNING" && "bg-blue-50 text-blue-700 border-blue-300 animate-pulse",
                            step.status === "READY" && "bg-indigo-50 text-indigo-700 border-indigo-300",
                            step.status === "PAUSED" && "bg-amber-50 text-amber-700 border-amber-300",
                            step.status === "CANCELLED" && "bg-rose-50 text-rose-600 border-rose-200 line-through",
                            step.status === "PLANNED" && "bg-slate-50 text-slate-600 border-slate-200"
                          )}
                        >
                          <option value="PLANNED">Chờ chạy</option>
                          <option value="READY">Sẵn sàng (Đã nhận BTP)</option>
                          <option value="SCHEDULED">Đã lên lịch</option>
                          <option value="RUNNING">Đang chạy</option>
                          <option value="COMPLETED">Hoàn thành</option>
                          <option value="PAUSED">Tạm dừng</option>
                          {/* Công đoạn không làm → Hủy bỏ (không có trạng thái "Bỏ qua" trong hệ thống, FB-D8900M) */}
                          <option value="CANCELLED">Hủy bỏ (Không làm)</option>
                        </select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Quick Handover button */}
                          {holdingWip > 0 && !isLastStep && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[11px] font-semibold border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => handleOpenHandover(step, nextStep, holdingWip)}
                              disabled={isPlanLocked || isSubmitting}
                              title={`Bàn giao BTP sang ${nextStep?.name || "công đoạn kế tiếp"}`}
                            >
                              <Truck className="h-3 w-3 mr-1 text-amber-600" /> Giao BTP
                            </Button>
                          )}

                          {/* Dynamic Action Buttons: Check-in / Check-out */}
                          {!isCompleted && !isRunning && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[11px] font-semibold border-blue-200 text-blue-700 hover:bg-blue-50"
                              onClick={() => handleCheckIn(step)}
                              disabled={isPlanLocked || isSubmitting}
                              title="Bắt đầu chạy công đoạn này"
                            >
                              <Play className="h-3 w-3 mr-1 fill-blue-600 text-blue-600" /> Bắt đầu
                            </Button>
                          )}

                          {isRunning && (
                            <Button
                              size="sm"
                              className="h-7 px-2 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                              onClick={() => handleOpenCheckOut(step)}
                              disabled={isPlanLocked || isSubmitting}
                              title="Báo cáo hoàn thành & nhập số lượng đạt/hỏng"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Check-out
                            </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isPlanLocked}
                            onClick={() => handleEdit(step)}
                            title="Chỉnh sửa cấu hình công đoạn"
                          >
                            <Edit className="h-3.5 w-3.5 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-rose-500 hover:bg-rose-50"
                            disabled={isPlanLocked}
                            onClick={() => {
                              setSelectedId(step.id);
                              setIsDeleteOpen(true);
                            }}
                            title="Xóa công đoạn"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={14}
                    className="text-center py-10 text-slate-500 bg-slate-50/50"
                  >
                    Chưa có công đoạn sản xuất nào. Bấm <strong>"Nạp từ Master"</strong> hoặc <strong>"Thêm công đoạn"</strong> để tạo mới.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog: Check-out Hoàn thành công đoạn */}
      <Dialog open={isCheckOutOpen} onOpenChange={setIsCheckOutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Báo Cáo Hoàn Thành Công Đoạn
            </DialogTitle>
          </DialogHeader>
          {activeCheckOutStep && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 p-3 rounded-lg border text-xs space-y-1">
                <div className="font-bold text-slate-800 text-sm">
                  {activeCheckOutStep.name || activeCheckOutStep.process?.name}
                </div>
                <div className="text-slate-600">
                  Thành phần: <span className="font-medium">{activeCheckOutStep.componentName}</span>
                </div>
                <div className="text-slate-600">
                  Số lượng chuẩn định mức: <span className="font-bold text-indigo-600 font-mono">{formatNumber(activeCheckOutStep.standardQuantity || qtyPlan || 0)}</span> pcs/tờ
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-emerald-700">SL Đạt chuẩn (OK) *</label>
                  <NumericInput
                    value={checkOutForm.actualQtyOk}
                    onChange={(e) => setCheckOutForm({ ...checkOutForm, actualQtyOk: Number(e.target.value) })}
                    className="border-emerald-300 font-mono font-bold text-sm bg-emerald-50/30"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-rose-700">SL Phế phẩm (NG)</label>
                  <NumericInput
                    value={checkOutForm.actualQtyNg}
                    onChange={(e) => setCheckOutForm({ ...checkOutForm, actualQtyNg: Number(e.target.value) })}
                    className="border-rose-200 font-mono text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Thời gian chạy (Phút)</label>
                  <NumericInput
                    value={checkOutForm.runTimeMinutes}
                    onChange={(e) => setCheckOutForm({ ...checkOutForm, runTimeMinutes: Number(e.target.value) })}
                    className="font-mono text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Ca làm việc</label>
                  <select
                    className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-xs shadow-sm"
                    value={checkOutForm.shift}
                    onChange={(e) => setCheckOutForm({ ...checkOutForm, shift: e.target.value })}
                  >
                    <option value="CA_1">Ca 1 (Sáng)</option>
                    <option value="CA_2">Ca 2 (Chiều)</option>
                    <option value="CA_3">Ca 3 (Đêm)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-700">Ghi chú kiểm soát / lỗi phát sinh</label>
                <Input
                  value={checkOutForm.notes}
                  onChange={(e) => setCheckOutForm({ ...checkOutForm, notes: e.target.value })}
                  placeholder="Nhập ghi chú hoặc lý do phế phẩm nếu có..."
                  className="text-xs"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setIsCheckOutOpen(false)} disabled={isSubmitting}>
              Hủy
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleConfirmCheckOut} disabled={isSubmitting}>
              {isSubmitting ? "Đang lưu..." : "Xác Nhận Check-out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Thêm / Sửa Công Đoạn */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {formData.id ? "Sửa công đoạn" : "Thêm công đoạn"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tên công đoạn</label>
              <SharedCombobox
                type="process"
                value={formData.stepId || ""}
                onChange={(id, option) =>
                  setFormData({
                    ...formData,
                    stepId: id,
                    name: option?.label || id,
                  })
                }
                placeholder="Chọn công đoạn chuẩn..."
                canAdd={true}
                onQuickAddClick={(name) =>
                  setFormData({ ...formData, stepId: name, name })
                }
                quickAddLabel="Nhập tay tên công đoạn"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Thuộc thành phần</label>
              <select
                className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm"
                value={formData.componentId || ""}
                onChange={(e) =>
                  setFormData({ ...formData, componentId: e.target.value })
                }
              >
                <option value="" disabled>
                  Chọn thành phần
                </option>
                {components.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.componentName}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Thứ tự</label>
              <NumericInput
                value={formData.stepOrder || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    stepOrder: Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Số lượng chuẩn (Định mức)</label>
                <NumericInput
                  value={formData.standardQuantity || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      standardQuantity: Number(e.target.value),
                    })
                  }
                  placeholder={String(qtyPlan || qtyOrder || 1000)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Bù hao (%)</label>
                <NumericInput
                  value={formData.plannedRunWastePct || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      plannedRunWastePct: Number(e.target.value),
                    })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Yêu cầu kỹ thuật</label>
              <Input
                value={formData.technicalRequirements || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalRequirements: e.target.value,
                  })
                }
                placeholder="VD: Tráng phủ bóng nhiệt độ 60 độ C, chồng màu chuẩn 0.1mm..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsFormOpen(false)}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang lưu..." : "Lưu thay đổi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Handover Dialog */}
      <Dialog open={isHandoverOpen} onOpenChange={setIsHandoverOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-amber-600" />
              Bàn Giao Bán Thành Phẩm (WIP)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Chuyển BTP từ <strong>{handoverStep?.name}</strong> sang <strong>{handoverNextStep?.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-lg flex items-center justify-between">
              <span className="text-slate-600 font-medium">Tồn BTP khả dụng tại trạm:</span>
              <span className="font-mono font-bold text-sm text-amber-700">
                {formatNumber(handoverForm.quantity)} tờ
              </span>
            </div>

            <div className="space-y-1.5">
              <label className="font-medium text-slate-700">Số lượng bàn giao (*)</label>
              <NumericInput
                value={handoverForm.quantity || ""}
                onChange={(e) =>
                  setHandoverForm({
                    ...handoverForm,
                    quantity: Number(e.target.value),
                  })
                }
                placeholder="Nhập số lượng chuyển..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="font-medium text-slate-700">Mã kiện Pallet</label>
                <Input
                  value={handoverForm.palletCode}
                  onChange={(e) =>
                    setHandoverForm({
                      ...handoverForm,
                      palletCode: e.target.value,
                    })
                  }
                  placeholder="VD: PLT-01, PALLET-A2..."
                  className="font-mono"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-medium text-slate-700">Người bàn giao</label>
                <Input
                  value={handoverForm.senderName}
                  onChange={(e) =>
                    setHandoverForm({
                      ...handoverForm,
                      senderName: e.target.value,
                    })
                  }
                  placeholder="Tên thợ máy giao..."
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="font-medium text-slate-700">Ghi chú</label>
              <Input
                value={handoverForm.notes}
                onChange={(e) =>
                  setHandoverForm({
                    ...handoverForm,
                    notes: e.target.value,
                  })
                }
                placeholder="Ghi chú thêm..."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsHandoverOpen(false)}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              onClick={handleConfirmHandover}
              disabled={isSubmitting || handoverForm.quantity <= 0}
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              {isSubmitting ? "Đang xử lý..." : "Xác nhận bàn giao"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Xóa công đoạn"
        description="Bạn có chắc chắn muốn xóa công đoạn này khỏi lệnh sản xuất? Thao tác này không thể hoàn tác."
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        open={Boolean(pendingOverlap)}
        onOpenChange={(open) => !open && setPendingOverlap(null)}
        title="Máy đã có việc trong khung giờ này"
        description={
          <span className="block space-y-1.5 text-left" data-testid="po-step-overlap-warning">
            <span className="block">Công đoạn sẽ chồng lịch với:</span>
            {(pendingOverlap?.conflicts || []).map((c) => (
              <span key={c.stepId} className="block font-mono text-xs text-rose-700">
                • {describeScheduleConflict(c)}
              </span>
            ))}
            <span className="block text-xs text-slate-600">
              Vẫn lưu thì công đoạn được khóa lịch (lập lịch tự động không dời) và máy chạy chồng — cần sắp lại tay.
            </span>
          </span>
        }
        confirmText="Vẫn lưu và khóa lịch"
        cancelText="Không lưu"
        variant="destructive"
        onConfirm={async () => {
          if (pendingOverlap) {
            await handleInstantPatchRoutingStep(pendingOverlap.stepId, { ...pendingOverlap.patch, confirmOverlap: true });
          }
        }}
      />
    </div>
  );
}
